import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ShoppingCart, Eye, Shield, FlaskConical, FileCheck, Truck, CheckCircle2, Building2, Lock } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { useOptimizedImages } from '../context/OptimizedImageContext';
import { useRegional } from '../context/RegionalContext';
import { productAPI, coaAPI } from '../lib/api';
import { toast } from 'sonner';
import COAModal from '../components/COAModal';
import SEOHead from '../components/SEO/SEOHead';
import { SEO_DATA } from '../components/SEO/seoData';
import RegionalPrice, { TaxInfo } from '../components/regional/RegionalPrice';

// Branded fallback image - light background with Amidex logo
const FALLBACK_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect fill='%23f8fafc' width='400' height='400'/%3E%3Ctext x='50%25' y='45%25' dominant-baseline='middle' text-anchor='middle' font-family='system-ui, sans-serif' font-size='48' font-weight='600' fill='%23e2e8f0'%3EAMIDEX%3C/text%3E%3Ctext x='50%25' y='58%25' dominant-baseline='middle' text-anchor='middle' font-family='system-ui, sans-serif' font-size='14' fill='%23cbd5e1'%3EBIOTECH%3C/text%3E%3C/svg%3E";

// RUO-Compliant Product Card Component
const RUOProductCard = ({ product }) => {
  const { addToCart, loading } = useCart();
  const { getOptimizedUrl } = useOptimizedImages();
  const [isHovered, setIsHovered] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Get the product image - use optimized version if available
  const getProductImage = () => {
    const originalUrl = product.image_url || 
      (product.images && product.images.length > 0 ? product.images[0] : null) ||
      (product.media && product.media.length > 0 ? product.media[0] : null);
    
    if (!originalUrl || imageError) return FALLBACK_IMAGE;
    
    // Try to get optimized WebP thumbnail (400px for grid)
    const optimizedUrl = getOptimizedUrl(originalUrl, { format: 'webp', thumbnail: 400 });
    return optimizedUrl || originalUrl;
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      setIsAdding(true);
      await addToCart(product.id, 1, product);
      toast.success(`${product.name} ${'added to cart'}`);
    } catch (error) {
      toast.error('Failed to add item to cart');
    } finally {
      setIsAdding(false);
    }
  };

  // Compliance tags - no health claims
  const complianceTags = [
    { label: 'cGMP', color: 'bg-blue-50 text-blue-700 border-blue-200' },
    { label: 'Standardized', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    { label: 'TDS Available', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  ];

  const productImage = getProductImage();
  
  // Use slug for SEO-friendly URLs, fallback to ID
  const productUrl = `/products/${product.slug || product.id}`;

  return (
    <Link
      to={productUrl}
      className="group relative bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all duration-300"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`product-card-${product.slug || product.id}`}
    >
      {/* Image Container - Minimal padding for larger product display */}
      <div className="relative aspect-square bg-gradient-to-br from-white via-slate-50/50 to-white">
        {/* Centered image with minimal padding to maximize product visibility */}
        <div className="absolute inset-1 sm:inset-2 flex items-center justify-center">
          <img
            src={productImage}
            alt={product.name}
            onError={handleImageError}
            loading="lazy"
            decoding="async"
            width="280"
            height="280"
            className={`max-w-full max-h-full object-contain transition-transform duration-500 ${
              isHovered ? 'scale-105' : 'scale-100'
            }`}
          />
        </div>
        
        {/* Quick Actions Overlay - pointer-events-none to allow card click through */}
        <div className={`absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center gap-3 transition-opacity duration-300 pointer-events-none ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}>
          <Button
            size="icon"
            className="w-11 h-11 rounded-full bg-slate-900 hover:bg-slate-800 text-white shadow-md pointer-events-auto"
            onClick={handleAddToCart}
            disabled={isAdding || loading || !product.in_stock}
            data-testid={`add-to-cart-${product.id}`}
          >
            <ShoppingCart className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            className="w-11 h-11 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white shadow-md pointer-events-auto"
          >
            <Eye className="h-4 w-4" />
          </Button>
        </div>

        {/* Stock Status - Positioned with top spacing */}
        {!product.in_stock && (
          <div className="absolute top-3 right-3">
            <span className="px-2 py-1 bg-slate-900 text-white text-xs font-medium rounded">
              {'Out of Stock'}
            </span>
          </div>
        )}
      </div>

      {/* Content - Compact for mobile 2-col grid */}
      <div className="p-2 sm:p-4 border-t border-slate-100">
        {/* Product Name & SKU */}
        <div className="mb-1 sm:mb-2">
          <h3 className="font-semibold text-sm sm:text-base text-slate-900 group-hover:text-cyan-600 transition-colors line-clamp-2">
            {product.name}
          </h3>
          {product.sku && (
            <p className="text-xs sm:text-sm text-slate-500 font-medium">SKU: {product.sku}</p>
          )}
        </div>
        <div className="mb-1.5 sm:mb-2">
          <RegionalPrice 
            productId={product.id} 
            basePriceUSD={product.price} 
            className="text-slate-900 text-sm sm:text-base font-bold"
          />
        </div>

        {/* Compliance Tags */}
        <div className="flex flex-wrap gap-1 sm:gap-1.5 mb-1.5 sm:mb-2">
          <span className="px-1.5 sm:px-2 py-0.5 text-[9px] sm:text-[10px] font-medium rounded border bg-emerald-50 text-emerald-700 border-emerald-200">
            {'Analytical Grade'}
          </span>
          <span className="hidden sm:inline-block px-2 py-0.5 text-[10px] font-medium rounded border bg-blue-50 text-blue-700 border-blue-200">
            {'B2B Institutional'}
          </span>
          <span className="hidden sm:inline-block px-2 py-0.5 text-[10px] font-medium rounded border bg-amber-50 text-amber-700 border-amber-200">
            {'TDS Available'}
          </span>
        </div>

        {/* Division indicator */}
        {product.division && (
          <div className="flex items-center text-xs text-slate-500">
            <FlaskConical className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-cyan-500 mr-1" />
            <span className="line-clamp-1">{product.division}</span>
          </div>
        )}
      </div>
    </Link>
  );
};

// Category Filter Button
const CategoryButton = ({ label, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-all ${
      isActive 
        ? 'bg-slate-900 text-white' 
        : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
    }`}
  >
    {label}
  </button>
);

// Trust Feature Card
const TrustCard = ({ icon: Icon, title, description }) => (
  <div className="text-center p-6">
    <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-4">
      <Icon className="w-7 h-7 text-blue-600" />
    </div>
    <h3 className="font-semibold text-slate-900 mb-2">{title}</h3>
    <p className="text-sm text-slate-500 leading-relaxed">{description}</p>
  </div>
);

// TDS Thumbnail for Preview Section
const COAThumbnail = ({ coa, onClick }) => (
  <button
    onClick={onClick}
    className="group relative bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-lg hover:border-blue-300 transition-all"
  >
    <div className="aspect-[3/4] bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="text-center">
        <FileCheck className="w-12 h-12 text-blue-500 mx-auto mb-2 group-hover:scale-110 transition-transform" />
        <p className="text-xs font-medium text-slate-700">{coa.product_name}</p>
        <p className="text-[10px] text-slate-500">Batch: {coa.batch_number}</p>
      </div>
    </div>
    <div className="p-3 border-t border-slate-100">
      <p className="text-xs text-slate-600 font-medium">View Certificate</p>
      <p className="text-[10px] text-slate-400">Click to preview</p>
    </div>
  </button>
);

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [sampleCOAs, setSampleCOAs] = useState([]);
  const [coaModalOpen, setCOAModalOpen] = useState(false);
  const [selectedCOA, setSelectedCOA] = useState(null);

  // Categories aligned with catalog divisions
  const categories = [
    { key: 'all', label: 'All Products' },
    { key: 'hardware', label: 'Laboratory Hardware & Equipment' },
    { key: 'consumables', label: 'Liquid Handling & Consumables' },
    { key: 'reagents', label: 'Analytical Reagents & Standards' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, coasRes] = await Promise.all([
          productAPI.getAll(),
          coaAPI.getAll().catch(() => ({ data: [] }))
        ]);
        setProducts(productsRes.data);
        setFilteredProducts(productsRes.data);
        
        // Get sample Technical Data Sheets for preview section
        if (coasRes.data && coasRes.data.length > 0) {
          setSampleCOAs(coasRes.data.slice(0, 3));
        } else {
          // Fallback sample Technical Data Sheets — generic SKU identifiers only
          setSampleCOAs([
            { id: '1', product_name: 'Analytical Reagent', batch_number: 'AMX-2026-001', purity: 'Certified Reagent Grade' },
            { id: '2', product_name: 'Analytical Reagent', batch_number: 'AMX-2026-002', purity: 'Certified Reagent Grade' },
            { id: '3', product_name: 'Analytical Reagent', batch_number: 'AMX-2026-003', purity: 'Certified Reagent Grade' },
          ]);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    let result = [...products];

    // Filter by search
    if (searchQuery) {
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (p.description && p.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Filter by division
    if (activeCategory !== 'all') {
      const divisionMap = {
        hardware: 'Laboratory Hardware & Equipment',
        consumables: 'Liquid Handling & Consumables',
        reagents: 'Analytical Reagents & Standards',
      };
      const division = divisionMap[activeCategory];
      if (division) {
        result = result.filter((p) => p.division === division);
      }
    }

    // Sort by name
    result.sort((a, b) => a.name.localeCompare(b.name));

    setFilteredProducts(result);
  }, [products, searchQuery, activeCategory]);

  const handleCOAClick = (coa) => {
    setSelectedCOA(coa);
    setCOAModalOpen(true);
  };

  // Find product for TDS modal
  const getProductForCOA = (coa) => {
    if (!coa || !products.length) return null;
    return products.find(p => 
      p.name?.toLowerCase() === coa.product_name?.toLowerCase() || 
      p.id === coa.product_id
    );
  };

  return (
    <div className="min-h-screen bg-white" data-testid="products-page">
      {/* SEO Head - noindex to prevent consumer search indexing */}
      <SEOHead
        title={SEO_DATA.products.title}
        description={SEO_DATA.products.description}
        canonical="/products"
        keywords={SEO_DATA.products.keywords}
        noindex={true}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Products', url: '/products' },
        ]}
      />

      {/* Category Filters - Sticky directly below header */}
      <div className="sticky top-[109px] z-40 bg-white border-b border-slate-100 shadow-sm">
        <div className="container mx-auto px-3 sm:px-6 lg:px-8 py-2 md:py-3">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            {/* Category Pills */}
            <div className="flex gap-2 overflow-x-auto pb-2 lg:pb-0 w-full lg:w-auto scrollbar-hide">
              {categories.map((cat) => (
                <CategoryButton
                  key={cat.key}
                  label={cat.label}
                  isActive={activeCategory === cat.key}
                  onClick={() => setActiveCategory(cat.key)}
                />
              ))}
            </div>

            {/* Search */}
            <div className="relative w-full lg:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={'Search products...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-10 rounded-full border-slate-200 bg-slate-50"
                data-testid="search-input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <section className="container mx-auto px-3 sm:px-6 lg:px-8 py-4 lg:py-10">
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="bg-slate-100 rounded-xl h-64 sm:h-80 animate-pulse" />
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="font-semibold text-lg text-slate-900 mb-2">
              {'No products found'}
            </h3>
            <p className="text-slate-500 mb-6">
              {'Try adjusting your search or filter criteria.'}
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery('');
                setActiveCategory('all');
              }}
            >
              {'Clear Filters'}
            </Button>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-500 mb-4">
              Showing {filteredProducts.length} laboratory products
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
              {filteredProducts.map((product) => (
                <RUOProductCard key={product.id} product={product} />
              ))}
            </div>
          </>
        )}
      </section>

      {/* Why Institutional laboratory personnel Choose AMIDEX - Trust Section */}
      <section className="bg-slate-50 py-16 border-t border-slate-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl text-slate-900 mb-3">
              Why Institutional laboratory personnel Choose AMIDEX
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto">
              Trusted by research institutions worldwide for quality and documentation
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <TrustCard
              icon={Building2}
              title="cGMP Manufacturing"
              description="All compounds synthesized in certified cGMP-compliant facilities with rigorous quality controls."
            />
            <TrustCard
              icon={FlaskConical}
              title="Independent Lab Testing"
              description="Every batch undergoes third-party analysis for identity, purity, and sterility verification."
            />
            <TrustCard
              icon={FileCheck}
              title="Batch Technical Data Sheets"
              description="Complete Technical Data Sheets included with every order for full traceability."
            />
            <TrustCard
              icon={Lock}
              title="Secure Supply Chain"
              description="Temperature-controlled storage and shipping ensures compound integrity throughout delivery."
            />
          </div>
        </div>
      </section>

      {/* TDS Preview Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-heading font-bold text-2xl text-slate-900 mb-3">
              Sample Technical Data Sheets
            </h2>
            <p className="text-slate-600 max-w-lg mx-auto">
              Preview our documentation standards. Every compound ships with a batch-specific TDS.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto">
            {sampleCOAs.map((coa) => (
              <COAThumbnail
                key={coa.id}
                coa={coa}
                onClick={() => handleCOAClick(coa)}
              />
            ))}
          </div>

          <p className="text-center text-xs text-slate-400 mt-6">
            Click any certificate to preview • Full TDS library available
          </p>
        </div>
      </section>

      {/* Bottom Compliance Notice */}
      <div className="bg-slate-100 py-4 border-t border-slate-200">
        <div className="container mx-auto px-4 text-center">
          <p className="text-xs text-slate-500">
            All products are sold strictly for laboratory institutional laboratory use only. Not for consumer or veterinary use. 
            Purchaser assumes all responsibility for proper handling and use.
          </p>
        </div>
      </div>

      {/* TDS Modal */}
      <COAModal
        isOpen={coaModalOpen}
        onClose={() => {
          setCOAModalOpen(false);
          setSelectedCOA(null);
        }}
        coa={selectedCOA}
        product={getProductForCOA(selectedCOA)}
      />
    </div>
  );
};

export default ProductsPage;
